import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Share2, Star, Trophy, TrendingUp, Zap, Copy, Check, Download } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';

const API = process.env.REACT_APP_BACKEND_URL;

const HIT_COLORS = {
  single: '#22c55e',
  double: '#3b82f6',
  triple: '#a855f7',
  home_run: '#ef4444',
  out: '#6b7280',
  flyout: '#6b7280',
  groundout: '#6b7280',
  strikeout: '#374151',
  strikeout_looking: '#374151',
  error: '#f59e0b',
  walk: '#0ea5e9',
  sacrifice: '#d97706'
};

const MiniSprayChart = ({ data }) => {
  if (!data?.length) return null;
  return (
    <div className="relative w-full aspect-square max-w-[200px] mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full" style={{ background: '#166534', borderRadius: '8px' }}>
        <path d="M 50 95 L 5 50 Q 50 -5 95 50 Z" fill="#14532d" />
        <path d="M 50 90 L 70 70 L 50 55 L 30 70 Z" fill="#c2885c" opacity="0.8" />
        <line x1="50" y1="95" x2="5" y2="50" stroke="white" strokeWidth="0.5" />
        <line x1="50" y1="95" x2="95" y2="50" stroke="white" strokeWidth="0.5" />
        <rect x="48" y="88" width="4" height="4" fill="white" transform="rotate(45 50 90)" />
        <rect x="68" y="68" width="3" height="3" fill="white" transform="rotate(45 70 70)" />
        <rect x="48" y="53" width="3" height="3" fill="white" transform="rotate(45 50 55)" />
        <rect x="28" y="68" width="3" height="3" fill="white" transform="rotate(45 30 70)" />
        {data.map((hit, idx) => (
          <circle key={idx} cx={hit.x} cy={hit.y} r="2.5"
            fill={HIT_COLORS[hit.result] || '#9ca3af'} stroke="white" strokeWidth="0.5">
            <title>{hit.result?.replace('_', ' ').toUpperCase()}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

const StatBadge = ({ label, value, highlight }) => (
  <div className={cn(
    "text-center py-2 px-3 rounded-lg",
    highlight ? "bg-amber-900/40 border border-amber-700/50" : "bg-stone-700/50"
  )}>
    <div className={cn("text-lg font-bold", highlight ? "text-amber-400" : "text-white")} data-testid={`stat-${label.toLowerCase()}`}>
      {value}
    </div>
    <div className="text-[10px] text-stone-400 uppercase tracking-wider">{label}</div>
  </div>
);

export default function HighlightReelPage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeKid, setActiveKid] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/highlights/reel/${shareToken}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [shareToken]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'My Kid\'s Highlight Reel', url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareApp = () => {
    const appUrl = 'https://diamondscorer.com';
    const msg = `Check out Diamond Scorer - live baseball & softball scoring for Little League. You can follow every pitch of our games, get push alerts for big plays, and see all the stats. ${appUrl}`;
    if (navigator.share) {
      navigator.share({ title: 'Diamond Scorer', text: msg, url: appUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(msg);
      toast.success('App link copied - paste it anywhere!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center text-white p-4">
        <Trophy size={48} className="text-stone-600 mb-4" />
        <h2 className="text-xl font-bold mb-2">Highlight Reel Not Found</h2>
        <p className="text-stone-400 text-sm mb-4">This link may have expired or been removed.</p>
        <Button onClick={() => navigate('/')} variant="outline" className="border-stone-600 text-stone-300">Go Home</Button>
      </div>
    );
  }

  const kid = data.kids[activeKid];

  return (
    <div className="min-h-screen bg-stone-950" data-testid="highlight-reel-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-amber-900/30 to-stone-950 border-b border-stone-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" className="text-stone-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <Button size="sm" onClick={handleShare}
              className="bg-amber-700 hover:bg-amber-600 text-white" data-testid="share-btn">
              {copied ? <><Check size={14} className="mr-1" /> Copied!</> : <><Share2 size={14} className="mr-1" /> Share</>}
            </Button>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Star size={16} className="text-amber-500" />
              <span className="text-amber-400 text-xs font-semibold tracking-wider uppercase">Highlight Reel</span>
              <Star size={16} className="text-amber-500" />
            </div>
            <h1 className="text-white text-2xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {data.team_name}
            </h1>
            <p className="text-stone-500 text-xs mt-0.5">by {data.parent_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Install App Prompt - grandparents arrive here first, need to install */}
        <InstallAppPrompt variant="banner" />

        {/* Kid Tabs (if multiple) */}
        {data.kids.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="kid-tabs">
            {data.kids.map((k, i) => (
              <button key={k.player.id} onClick={() => setActiveKid(i)}
                data-testid={`kid-tab-${i}`}
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all",
                  i === activeKid
                    ? "bg-amber-600 text-white shadow-lg shadow-amber-600/20"
                    : "bg-stone-800 text-stone-400 hover:bg-stone-700"
                )}>
                #{k.player.jersey_number || '?'} {k.player.name}
              </button>
            ))}
          </div>
        )}

        {kid && (
          <>
            {/* Player Header Card */}
            <Card className="border-0 bg-stone-900 overflow-hidden" data-testid="player-header-card">
              <div className="bg-gradient-to-r from-amber-900/40 to-stone-900 p-5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-amber-700/40 border-2 border-amber-600 flex items-center justify-center">
                    <span className="text-2xl font-extrabold text-amber-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {kid.player.jersey_number || '?'}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-white text-xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="player-name">
                      {kid.player.name}
                    </h2>
                    <p className="text-stone-400 text-sm">{data.team_name}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Stats Grid */}
            <Card className="border-0 bg-stone-900" data-testid="stats-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <TrendingUp size={14} className="text-amber-500" /> SEASON STATS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <StatBadge label="AVG" value={kid.stats.avg.toFixed(3)} highlight={kid.stats.avg >= 0.300} />
                  <StatBadge label="OPS" value={kid.stats.ops.toFixed(3)} highlight={kid.stats.ops >= 0.800} />
                  <StatBadge label="HR" value={kid.stats.hr} highlight={kid.stats.hr > 0} />
                  <StatBadge label="RBI" value={kid.stats.rbi} highlight={kid.stats.rbi >= 5} />
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <StatBadge label="G" value={kid.stats.games} />
                  <StatBadge label="AB" value={kid.stats.ab} />
                  <StatBadge label="H" value={kid.stats.h} />
                  <StatBadge label="BB" value={kid.stats.bb} />
                  <StatBadge label="K" value={kid.stats.k} />
                </div>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <StatBadge label="1B" value={kid.stats.singles} />
                  <StatBadge label="2B" value={kid.stats.doubles} />
                  <StatBadge label="3B" value={kid.stats.triples} />
                  <StatBadge label="SLG" value={kid.stats.slg.toFixed(3)} />
                </div>
              </CardContent>
            </Card>

            {/* Spray Chart */}
            {kid.spray_data.length > 0 && (
              <Card className="border-0 bg-stone-900" data-testid="spray-chart-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <Zap size={14} className="text-amber-500" /> SPRAY CHART
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MiniSprayChart data={kid.spray_data} />
                  <div className="flex justify-center gap-3 mt-3 flex-wrap">
                    {[['single', 'Singles'], ['double', 'Doubles'], ['triple', 'Triples'], ['home_run', 'HR'], ['out', 'Outs']].map(([key, label]) => (
                      <div key={key} className="flex items-center gap-1 text-xs text-stone-400">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: HIT_COLORS[key] }} />
                        {label}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Plays / Highlights */}
            {kid.key_plays.length > 0 && (
              <Card className="border-0 bg-stone-900" data-testid="key-plays-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <Trophy size={14} className="text-amber-500" /> KEY MOMENTS
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[300px] overflow-y-auto">
                  <div className="space-y-2">
                    {kid.key_plays.map((play, idx) => (
                      <div key={idx} className="flex items-start gap-3 py-2 px-3 bg-stone-800/50 rounded-lg border-l-2 border-amber-600" data-testid={`key-play-${idx}`}>
                        <div className="shrink-0 w-8 h-8 rounded-full bg-amber-700/30 flex items-center justify-center">
                          <Star size={14} className="text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold">
                            {play.type === 'highlight' ? play.description : play.type}
                          </p>
                          <p className="text-stone-500 text-xs">
                            {play.opponent && `vs ${play.opponent}`} {play.game_date && `- ${play.game_date}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No data state */}
            {kid.stats.ab === 0 && (
              <Card className="border-0 bg-stone-900">
                <CardContent className="py-8 text-center">
                  <Trophy size={32} className="mx-auto text-stone-600 mb-2" />
                  <p className="text-stone-400 text-sm">No game data yet</p>
                  <p className="text-stone-600 text-xs mt-1">Stats will appear after games are played</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Share Diamond Scorer - viral loop for grandparents */}
        <Card
          className="border-0 bg-gradient-to-br from-green-900 to-green-950 overflow-hidden"
          data-testid="share-app-card"
        >
          <CardContent className="p-5 text-center space-y-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
              <Star size={10} /> Love this?
            </div>
            <h3 className="text-white text-lg font-extrabold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              SHARE DIAMOND SCORER WITH FAMILY
            </h3>
            <p className="text-green-200/80 text-xs leading-snug">
              Every grandparent, aunt, uncle and friend can follow live - no app store download required.
            </p>
            <Button
              onClick={shareApp}
              className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold"
              data-testid="share-app-btn"
            >
              <Share2 size={14} className="mr-2" /> Share Diamond Scorer
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-stone-600 text-xs">Diamond Scorer · diamondscorer.com</p>
        </div>
      </div>
    </div>
  );
}
